/*
 * A Blank variant is a separate purchase, not an unfinished Customily design.
 * Keep the app's normal Personalised route. Blank uses a clean native form POST:
 * no editor properties, app submit handlers, fetch or XHR interception.
 */
(() => {
  if (window.byardsBlankPurchase) return;
  window.byardsBlankPurchase = true;

  const roots = new Map();
  let appWillLoad = false;
  const appControls = '#customily-personalize-button, .customily-personalize-button, #customily-cart-btn';
  const normalise = (value) => String(value || '').trim().toLowerCase();

  class BlankPurchase {
    constructor(root) {
      this.root = root;
      this.submitting = false;
      this.previousMode = '';
      this.appExpected = root.dataset.bbAppRequired === 'true' || appWillLoad;
      this.appFailed = false;
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(root, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['id', 'class', 'style', 'hidden', 'value', 'aria-selected', 'checked']
      });
      this.refresh();
    }

    snapshot() {
      const form = this.root.querySelector('.js-product-form-main');
      const picker = this.root.querySelector('variant-picker');
      let config;
      try { config = JSON.parse(this.root.querySelector('[data-bb-variant]').textContent); }
      catch (_) { return { mode: 'pending' }; }
      if (!form || !picker) return { mode: 'pending' };

      const selected = Array.from(picker.querySelectorAll('.option-selector:not(.option-selector--custom)')).map((selector) => {
        const option = selector.dataset.selectorType === 'dropdown'
          ? selector.querySelector('.custom-select__option[aria-selected="true"]')
          : selector.querySelector('input:checked');
        return option ? (option.dataset.value ?? option.value) : null;
      });
      if (!selected.length || selected.some((value) => !value)) return { mode: 'unselected' };
      const variant = config.variant;
      if (!variant) return { mode: 'unavailable' };
      // Both the visible options and native ID must agree with the server-rendered variant.
      if (selected.length !== variant.options.length
        || selected.some((value, index) => value !== variant.options[index])
        || String(form.querySelector('[name="id"]')?.value) !== String(variant.id)) {
        return { mode: 'pending' };
      }
      const choice = normalise(variant.options[config.optionIndex]);
      // Any other configured choice keeps its existing app flow.
      const mode = choice === 'blank' ? 'blank' : 'personalised';
      return { mode, variant, form };
    }

    appReady() {
      return Boolean(this.root.querySelector(appControls)
        || this.root.querySelector('#customily-options input, #customily-options select, #cl_optionsapp input, #cl_optionsapp select'));
    }

    refresh() {
      if (!this.root.isConnected) {
        this.observer.disconnect();
        roots.delete(this.root);
        return;
      }
      const state = this.snapshot();
      if (this.appReady()) this.appExpected = true;
      this.root.dataset.bbPurchaseMode = state.mode;
      this.root.dataset.bbAppReady = String(this.appReady());
      this.root.dataset.bbAppRequired = String(this.appExpected);
      const blank = state.mode === 'blank';
      document.body.toggleAttribute('data-bb-blank-active', blank);
      if (blank && this.previousMode !== 'blank') {
        window.dispatchEvent(new Event('customily-modal-close'));
        window.dispatchEvent(new Event('customily-options-collapse'));
      }
      this.previousMode = state.mode;
      const button = this.root.querySelector('[data-bb-blank-add]');
      if (button) {
        button.disabled = !blank || !state.variant?.available || this.submitting;
        const label = this.submitting ? 'Adding…' : state.variant?.available === false ? 'Sold out' : 'Add to cart';
        if (button.textContent !== label) button.textContent = label;
      }
      const status = this.root.querySelector('[data-bb-purchase-status]');
      if (status) {
        const message = state.mode === 'pending' ? 'Updating your selection…'
          : state.mode === 'unselected' ? 'Please choose your options.'
            : state.mode === 'unavailable' ? 'This combination is unavailable. Please choose another option.'
              : state.mode === 'personalised' && !state.variant?.available ? 'This option is sold out.'
                : state.mode === 'personalised' && this.appExpected && !this.appReady()
                  ? (this.appFailed ? 'Personalisation could not load. Please refresh the page and try again.' : 'Loading personalisation…') : '';
        if (status.textContent !== message) status.textContent = message;
        if (status.hidden !== !message) status.hidden = !message;
      }
      return state;
    }

    addBlank() {
      const state = this.refresh();
      if (this.submitting || state?.mode !== 'blank' || !state.variant?.available) return;
      const input = this.root.querySelector('[data-bb-quantity]');
      const rule = state.variant.quantityRule || {};
      const minimum = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      const maximum = rule.max == null ? Infinity : Number(rule.max);
      const quantity = input ? Number(input.value) : minimum;
      if (input) input.setCustomValidity('');
      if (!Number.isSafeInteger(quantity) || quantity < minimum || quantity > maximum || (quantity - minimum) % increment !== 0) {
        if (input) {
          input.setCustomValidity(`Please enter a valid quantity (minimum ${minimum}, in steps of ${increment}${Number.isFinite(maximum) ? `, maximum ${maximum}` : ''}).`);
          input.reportValidity();
        }
        return;
      }
      if (input && !input.reportValidity()) return;
      const action = new URL(this.root.dataset.bbCartUrl, window.location.origin);
      if (action.origin !== window.location.origin || !action.pathname.endsWith('/cart/add')) return;

      const cleanForm = document.createElement('form');
      cleanForm.method = 'post';
      cleanForm.action = action.href;
      cleanForm.hidden = true;
      // No ID: fields injected into or associated with the original form cannot join this request.
      for (const [name, value] of Object.entries({ id: state.variant.id, quantity })) {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.value = String(value);
        cleanForm.append(field);
      }
      document.body.append(cleanForm);
      this.submitting = true;
      this.refresh();
      try {
        HTMLFormElement.prototype.submit.call(cleanForm);
      } catch (_) {
        this.submitting = false;
        this.refresh();
        const status = this.root.querySelector('[data-bb-purchase-status]');
        if (status) {
          status.textContent = 'We could not open your basket. Please try again.';
          status.hidden = false;
        }
      } finally {
        // Submission constructs the request synchronously before returning.
        cleanForm.remove();
      }
    }
  }

  const start = () => {
    document.querySelectorAll('[data-byards-blank-product]').forEach((root) => {
      if (!roots.has(root)) roots.set(root, new BlankPurchase(root));
    });
  };
  const controllerFor = (element) => roots.get(element?.closest?.('[data-byards-blank-product]'));

  // Capture before Customily/theme listeners. Changes are gated immediately, before section fetches.
  window.addEventListener('change', (event) => {
    const controller = controllerFor(event.target);
    if (controller && (event.target.closest('variant-picker') || event.target.name === 'id')) controller.refresh();
  }, true);
  window.addEventListener('input', (event) => {
    if (event.target.matches?.('[data-bb-quantity]')) event.target.setCustomValidity('');
  });
  window.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-bb-blank-add]');
    if (button) {
      event.preventDefault();
      event.stopImmediatePropagation();
      controllerFor(button)?.addBlank();
      return;
    }
    const control = event.target.closest?.(appControls);
    if (control) {
      const controller = controllerFor(control) || Array.from(roots.values())[0];
      const state = controller?.refresh();
      if (controller && (state?.mode !== 'personalised' || !state.variant?.available)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
  }, true);
  window.addEventListener('submit', (event) => {
    const controller = controllerFor(event.target);
    if (!controller || !event.target.matches('.js-product-form-main')) return;
    const state = controller.refresh();
    if (state?.mode !== 'personalised' || (controller.appExpected && !controller.appReady()) || !state.variant?.available) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (state?.mode === 'blank') controller.addBlank();
    }
  }, true);
  document.addEventListener('on:variant:change', (event) => controllerFor(event.target)?.refresh());
  document.addEventListener('shopify:section:load', start);
  // Products using a different personalisation method keep their existing route.
  // Positive app detection gates only confirmed Customily controls while they load.
  window.addEventListener('customily-app-will-load', () => {
    appWillLoad = true;
    roots.forEach((controller) => { controller.appExpected = true; controller.appFailed = false; controller.refresh(); });
  });
  window.addEventListener('customily-app-wont-load', () => {
    roots.forEach((controller) => { controller.appFailed = true; controller.refresh(); });
  });
  ['customily-options-container-ready', 'customily-options-loaded', 'customily-variation-change-finished'].forEach((name) => {
    window.addEventListener(name, () => roots.forEach((controller) => controller.refresh()));
  });
  window.addEventListener('pageshow', () => roots.forEach((controller) => {
    controller.submitting = false;
    controller.refresh();
  }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
